import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";

const AXIS_COLOR = "var(--neutral-inactive)";

export interface BarSeries {
  dataKey: string;
  name: string;
  color: string;
}

function GroupedTooltip({
  active,
  payload,
  label,
  suffix,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  suffix: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{label}</p>
      <div className="mt-1 space-y-0.5">
        {payload.map((entry) => (
          <p key={entry.name} className="flex items-center gap-2 text-muted-foreground">
            <span className="size-2" style={{ backgroundColor: entry.color }} aria-hidden />
            {entry.name}
            <span className="ml-auto tabular-nums text-foreground">
              {entry.value.toFixed(2)}
              {suffix}
            </span>
          </p>
        ))}
      </div>
    </ChartTooltipShell>
  );
}

export function GroupedBarChart<T extends Record<string, unknown>>({
  data,
  categoryKey,
  series,
  suffix = "%",
  height = 340,
  layout = "vertical-bars",
  categoryWidth = 160,
  onBarClick,
  cellColorKey,
}: {
  data: T[];
  categoryKey: string;
  series: BarSeries[];
  suffix?: string;
  height?: number;
  /** "vertical-bars" = bars grow up; "horizontal-bars" = bars grow right */
  layout?: "vertical-bars" | "horizontal-bars";
  categoryWidth?: number;
  /** When set, bars become clickable and receive the row behind them. */
  onBarClick?: (row: T, dataKey: string) => void;
  /**
   * Field on each row holding that bar's OWN colour, for charts whose rows are
   * different quantities rather than the same quantity across categories.
   *
   * Honoured only for a single series, and that restriction is the point: with two
   * or more series the colour is what tells the reader which series a bar belongs
   * to, so colouring per row would destroy the legend's meaning. Rows missing the
   * field fall back to the series colour.
   */
  cellColorKey?: string;
}) {
  const horizontal = layout === "horizontal-bars";

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
          barGap={2}
        >
          <CartesianGrid
            stroke="var(--border)"
            strokeDasharray="2 4"
            vertical={horizontal}
            horizontal={!horizontal}
          />
          <XAxis
            type={horizontal ? "number" : "category"}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            {...(horizontal
              ? { tickFormatter: (value: number) => `${value.toFixed(1)}${suffix}` }
              : { dataKey: categoryKey })}
          />
          <YAxis
            type={horizontal ? "category" : "number"}
            width={horizontal ? categoryWidth : 56}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
            {...(horizontal
              ? { dataKey: categoryKey }
              : { tickFormatter: (value: number) => `${value.toFixed(1)}${suffix}` })}
          />
          <Tooltip
            content={<GroupedTooltip suffix={suffix} />}
            cursor={{ fill: "var(--border)", fillOpacity: 0.25 }}
          />
          {series.length > 1 ? (
            <Legend
              wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground)" }}
              iconType="square"
            />
          ) : null}
          {series.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              name={bar.name}
              fill={bar.color}
              isAnimationActive={false}
              {...(onBarClick
                ? {
                    cursor: "pointer",
                    onClick: (entry: unknown) => {
                      const payload = (entry as { payload?: T })?.payload;
                      if (payload) onBarClick(payload, bar.dataKey);
                    },
                  }
                : {})}
            >
              {cellColorKey && series.length === 1
                ? data.map((row, index) => {
                    const own = row[cellColorKey];
                    return (
                      <Cell
                        key={index}
                        fill={typeof own === "string" && own ? own : bar.color}
                      />
                    );
                  })
                : null}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
